import { Suspense, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { ReservationsProvider } from "@/contexts/ReservationsContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { RouteErrorBoundary } from "@/components/RouteErrorBoundary";
import { lazyWithReload, clearLazyReloadGuard } from "@/lib/lazy-with-reload";
import { OwnerFilterProvider } from "@/contexts/OwnerFilterContext";
import { Outlet } from "react-router-dom";
// Eager: landing + auth (entry path, must render fast for LCP)
import Auth from "./pages/Auth";
import Index from "./pages/Index";

// Lazy: all other routes are split into their own chunks.
// lazyWithReload auto-reloads (once) when a stale chunk hash 404s after a
// redeploy — fixes the "white page after coming back later" issue.
const ReservationsReport = lazyWithReload(() => import("./pages/ReservationsReport"));
const Calendar = lazyWithReload(() => import("./pages/Calendar"));

const GuestRatings = lazyWithReload(() => import("./pages/GuestRatings"));
const GuestReviews = lazyWithReload(() => import("./pages/GuestReviews"));
const ACL = lazyWithReload(() => import("./pages/ACL"));
const ImportUsers = lazyWithReload(() => import("./pages/ImportUsers"));
const AmCreateUser = lazyWithReload(() => import("./pages/AmCreateUser"));
const BulkImport = lazyWithReload(() => import("./pages/BulkImport"));
const SyncReviews = lazyWithReload(() => import("./pages/SyncReviews"));
const Scheduler = lazyWithReload(() => import("./pages/admin/Scheduler"));
const ChangePassword = lazyWithReload(() => import("./pages/ChangePassword"));
const MinRateCalculator = lazyWithReload(() => import("./pages/MinRateCalculator"));
const PmSetup = lazyWithReload(() => import("./pages/PmSetup"));
const TakeAction = lazyWithReload(() => import("./pages/admin/TakeAction"));
const SecretsClients = lazyWithReload(() => import("./pages/admin/SecretsClients"));
const SecretsOwners = lazyWithReload(() => import("./pages/admin/SecretsOwners"));
const SecretsClientsTutorial = lazyWithReload(() => import("./pages/admin/SecretsClientsTutorial"));
const SecretsOwnersTutorial = lazyWithReload(() => import("./pages/admin/SecretsOwnersTutorial"));

const OwnerPerformance = lazyWithReload(() => import("./pages/owner/OwnerPerformance"));

const OwnerNetRevenue = lazyWithReload(() => import("./pages/owner/OwnerNetRevenue"));
const OwnerGlossary = lazyWithReload(() => import("./pages/owner/OwnerGlossary"));
const OwnerDocs = lazyWithReload(() => import("./pages/owner/OwnerDocs"));
const OwnerLinks = lazyWithReload(() => import("./pages/owner/OwnerLinks"));

const SharedSnapshotView = lazyWithReload(() => import("./pages/SharedSnapshotView"));
const SharedLinksManager = lazyWithReload(() => import("./pages/SharedLinksManager"));

function isAuthError(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err ?? "");
  const code = (err as { code?: string; status?: number } | null)?.code;
  const status = (err as { status?: number } | null)?.status;
  return (
    status === 401 ||
    status === 403 ||
    code === "PGRST301" ||
    /JWT expired|invalid JWT|JWSError|token is expired|Unauthorized/i.test(msg)
  );
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: true,
      // Retry once on auth errors too: gives the fetch-level refresh a
      // chance to complete before the failure surfaces to the UI. Cap total
      // attempts at 2 for auth errors, 3 for everything else.
      retry: (failureCount, error) => {
        if (isAuthError(error)) return failureCount < 1;
        return failureCount < 2;
      },
      retryDelay: (attempt, error) =>
        isAuthError(error) ? 400 : Math.min(1000 * 2 ** attempt, 8000),
    },
  },
});

const RouteFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
  </div>
);

const App = () => {
  // Once the app successfully mounts, clear the one-shot reload guard so
  // future stale-chunk failures can trigger another recovery reload.
  useEffect(() => {
    clearLazyReloadGuard();
  }, []);

  return (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <ReservationsProvider>
          <OwnerFilterProvider>
          <RouteErrorBoundary>
          <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/share/:slug" element={<SharedSnapshotView />} />
            <Route
              path="/shared-links"
              element={
                <ProtectedRoute>
                  <SharedLinksManager />
                </ProtectedRoute>
              }
            />
            <Route
              path="/reservations"
              element={
                <ProtectedRoute requirePage="reservations">
                  <ReservationsReport />
                </ProtectedRoute>
              }
            />
            <Route
              path="/calendar"
              element={
                <ProtectedRoute requirePage="calendar">
                  <Calendar />
                </ProtectedRoute>
              }
            />
            <Route
              path="/guest-ratings"
              element={
                <ProtectedRoute requirePage="guest-ratings">
                  <GuestRatings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/guest-reviews"
              element={
                <ProtectedRoute requirePage="guest-reviews">
                  <GuestReviews />
                </ProtectedRoute>
              }
            />
            <Route path="/reviews" element={<Navigate to="/guest-ratings" replace />} />
            <Route
              path="/min-rate-calculator"
              element={
                <ProtectedRoute requirePage="min-rate-calculator">
                  <MinRateCalculator />
                </ProtectedRoute>
              }
            />
            <Route
              path="/pm-setup"
              element={
                <ProtectedRoute requirePage="pm-setup">
                  <PmSetup />
                </ProtectedRoute>
              }
            />


            <Route
              path="/acl"
              element={
                <ProtectedRoute requirePage="acl">
                  <ACL />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/import-users"
              element={
                <ProtectedRoute requirePage="admin-import-users">
                  <ImportUsers />
                </ProtectedRoute>
              }
            />
            <Route
              path="/am/create-user"
              element={
                <ProtectedRoute requirePage="am-users">
                  <AmCreateUser />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/bulk-import"
              element={
                <ProtectedRoute requirePage="admin-bulk-import">
                  <BulkImport />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/sync-reviews"
              element={
                <ProtectedRoute requirePage="admin-sync-reviews">
                  <SyncReviews />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/change-password"
              element={
                <ProtectedRoute requirePage="admin-change-password">
                  <ChangePassword />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/take-action"
              element={
                <ProtectedRoute requirePage="admin-take-action">
                  <TakeAction />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/scheduler"
              element={
                <ProtectedRoute requirePage="admin-scheduler">
                  <Scheduler />
                </ProtectedRoute>
              }
            />
            <Route
              path="/secrets/clients"
              element={
                <ProtectedRoute requirePage="admin-secrets-clients">
                  <SecretsClients />
                </ProtectedRoute>
              }
            />
            <Route
              path="/secrets/clients/tutorial"
              element={
                <ProtectedRoute requirePage="admin-secrets-clients">
                  <SecretsClientsTutorial />
                </ProtectedRoute>
              }
            />
            <Route
              path="/secrets/owners"
              element={
                <ProtectedRoute requirePage="admin-secrets-owners">
                  <SecretsOwners />
                </ProtectedRoute>
              }
            />
            <Route
              path="/secrets/owners/tutorial"
              element={
                <ProtectedRoute requirePage="admin-secrets-owners">
                  <SecretsOwnersTutorial />
                </ProtectedRoute>
              }
            />
            <Route path="/owner" element={<Navigate to="/performance" replace />} />
            <Route path="/owner/overview" element={<Navigate to="/performance" replace />} />
            <Route path="/overview" element={<Navigate to="/performance" replace />} />
            <Route path="/owner/performance" element={<Navigate to="/performance" replace />} />
            
            <Route path="/owner/reviews" element={<Navigate to="/guest-ratings" replace />} />
            <Route path="/owner/net-revenue" element={<Navigate to="/net-revenue" replace />} />
            <Route path="/owner/glossary" element={<Navigate to="/glossary" replace />} />
            <Route element={<Suspense fallback={<RouteFallback />}><Outlet /></Suspense>}>
              <Route path="/performance" element={<ProtectedRoute requirePage="owner-performance"><OwnerPerformance /></ProtectedRoute>} />
              
              
              <Route path="/net-revenue" element={<ProtectedRoute requirePage="net-revenue"><OwnerNetRevenue /></ProtectedRoute>} />
              <Route path="/glossary" element={<ProtectedRoute requirePage="glossary"><OwnerGlossary /></ProtectedRoute>} />
              <Route path="/docs" element={<ProtectedRoute requirePage="glossary"><OwnerDocs /></ProtectedRoute>} />
              <Route path="/links" element={<ProtectedRoute requirePage="links"><OwnerLinks /></ProtectedRoute>} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </Suspense>
          </RouteErrorBoundary>
          </OwnerFilterProvider>
          </ReservationsProvider>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
  );
};

export default App;
